import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../contexts/CartContext';
import { getShippingQuote, getTaxes, getShippingConfirmation, getPackingInfo, getCouponConfirmation, decreaseCoupon, decreaseDrop, getDropEnded } from '../controllers/CartConnectionLayer';
import "../style/AppPages.css";
import "../style/Cart-Checkout.css";
import CheckoutSummary from '../components/CheckoutSummary';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import MessageModal from '../components/MessageModal';
import { useAuth } from "../contexts/AuthContext";
import { fetchStripeUserData, fetchUserDataFromBd, saveOrderToBD, addShippingAddressBD } from '../controllers/AccountConnectionLayer';
import { notifyError } from "../controllers/Toaster";
import { CountrySelect, StateSelect, GetCountries, GetState } from 'react-country-state-city';
import "react-country-state-city/dist/react-country-state-city.css";
import SmallerLoading from '../components/SmallerLoading';
import useViewport from '../hooks/useViewport';
import { debounce } from 'lodash'; // Import lodash debounce method
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ asyncCartItems, taxes, selectedShippingOption, totalCost, shippingData, packageDetails, preTotal, stripe_id, saveAddress, couponCode, email }) => {
    
    const stripe = useStripe();
    const elements = useElements();
    const [errorMessage, setErrorMessage] = useState('');
    const [dropEnded, setDropEnded] = useState(null);
    const [hasCustoms, setHasCustoms] = useState(false);
    const { t } = useTranslation();
    const [localCouponCode, setLocalCouponCode] = useState(couponCode);
    const [localEmail, setLocalEmail] = useState(email);

    useEffect(() => {
        setLocalCouponCode(couponCode);
    }, [couponCode]);

    useEffect(() => {
        setLocalEmail(email);
    }, [email]);

    useEffect(() => {
        const checkDropStatus = async () => {
            try {
                const ended = await getDropEnded();
                setDropEnded(ended);
            } catch (err) {
                console.error("Failed to check drop status", err);
                setDropEnded(false);
            }
        };
        checkDropStatus();
    }, []);

    useEffect(() => {
        setHasCustoms(asyncCartItems.some(item => item.isCustom === true));
    }, [asyncCartItems]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        // setLoading(true);
    
        if (!stripe || !elements) return;

        const courierData = {
            courierName: selectedShippingOption.courier_name,
            courierCode: selectedShippingOption.postage_type,
        };

        try {
            // Try to redeem coupon BEFORE payment confirmation
            var couponValue = null
            var cardValue = null;
            
            if (localCouponCode && localEmail) {

                const couponResult = await getCouponConfirmation(localCouponCode, localEmail, shippingData.country);
            
                if (couponResult.success) {
                    couponValue = couponResult.discount_amount;
                    cardValue = couponResult.card_amount;
                }
            }

            const checkoutCartData = {
                asyncCartItems,
                preTotal,
                taxes,
                selectedShippingOption,
                totalCost, // Reflects updated cost if coupon applied
                localCouponCode,
                couponValue,
                cardValue
            };

            localStorage.setItem('checkoutCartData', JSON.stringify(checkoutCartData));
            
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {},
                redirect: 'if_required',
            });
    
            if (error) {
                console.error('Payment confirmation failed:', error);
                setErrorMessage('Payment failed. Please try again.');
                return;
            }
    
            // setLoading(true);
    
            if (paymentIntent?.status === 'succeeded') {
                await getShippingConfirmation(shippingData, courierData);

                const parsedItems = asyncCartItems.map(item => ({
                    previewUrl: item.previewUrl,
                    name: item.name,
                    totalPrice: (
                        (parseFloat(item.selectedSize?.price || 0) + 
                        (typeof item.selectedFirmness === 'object' ? parseFloat(item.selectedFirmness?.price || 0) : 0) + 
                        parseFloat(item.selectedOption?.price || 0) + 
                        parseFloat(item.selectedColor?.price || 0)) * item.numberOfItems
                    ).toFixed(2),
                    currency: item.cc,
                    symbol: item.cs,
                    selectedSize: item.selectedSize?.description,
                    selectedColor: item.selectedColor?.description,
                    selectedOption: item.selectedOption?.description,
                    numberOfItems: item.numberOfItems,
                }));
                
                const response = await saveOrderToBD(paymentIntent, parsedItems, t);

                if (response.success) {
                    if (saveAddress) {
                        await addShippingAddressBD(
                            shippingData.firstName, 
                            shippingData.lastName, 
                            shippingData.phone, shippingData.address, shippingData.unit, shippingData.city, shippingData.country, shippingData.province, shippingData.zip, shippingData.email, stripe_id, t );
                    }

                    var couponUseResult = await decreaseCoupon(localCouponCode, localEmail);
    
                    if (couponUseResult && !couponUseResult.success) {
                        throw new Error(couponUseResult.message || 'Failed to redeem coupon');
                    }

                    if (hasCustoms) {
                        couponUseResult = await decreaseDrop();

                        if (!couponUseResult.success) {
                            throw new Error(couponUseResult.message || 'Custom orders are currently closed');
                        }
                    }

                    window.location.href = `/order-complete?payment_intent=${paymentIntent.id}`;
                } else {
                    throw new Error('Error saving order to database');
                }
            }
        } 
        catch (error) {
            console.error('An unexpected error occurred:', error.message);
            setErrorMessage(error.message || 'An error occurred. Please try again later.');
        }
    
        // setLoading(false);
    };
    
    return (
        <div>
            {(dropEnded && hasCustoms) ? (
                    <p id="please-remove">
                        {t("checkout.removeDropLabel")}
                    </p>
                
                ) : (
                    <form onSubmit={handleSubmit}>
                        {errorMessage && <p className="error-message">{errorMessage}</p>}
                        <PaymentElement />
                        <button disabled={!stripe || !elements}>{t("checkout.payLabel")}</button>
                    </form>
                )}
        </div>
    );
};

const CheckoutPage = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { isSmallView } = useViewport();
    const { cartItems } = useCart();
    const { currencySymbol, formatPrice, formatCadPrice, calculateConvertedTotalPrice, selectedCurrency } = useCurrency();
    const [clientSecret, setClientSecret] = useState('');
    const [asyncCartItems, setAsyncCartItems] = useState([]);

    const [destinationFirstName, setDestinationFirstName] = useState('');
    const [destinationLastName, setDestinationLastName] = useState('');
    const [destinationPhoneNumber, setDestinationPhoneNumber] = useState('');
    const [destinationEmail, setDestinationEmail] = useState('');

    const [destinationAddress, setDestinationAddress] = useState('');
    const [destinationUnit, setDestinationUnit] = useState('');
    const [destinationCity, setDestinationCity] = useState('');
    const [destinationCountry, setDestinationCountry] = useState('');
    const [destinationProvince, setDestinationProvince] = useState('');
    const [destinationZip, setDestinationZip] = useState('');

    const [billingAddress, setBillingAddress] = useState('');
    const [billingUnit, setBillingUnit] = useState('');
    const [billingCity, setBillingCity] = useState('');
    const [billingCountry, setBillingCountry] = useState('');
    const [billingProvince, setBillingProvince] = useState('');
    const [billingZip, setBillingZip] = useState('');

    const [showBillingAddress, setShowBillingAddress] = useState(false);

    const [loading, setLoading] = useState(true);
    const [shippingLoading, setShippingLoading] = useState(false);

    const [shippingOptions, setShippingOptions] = useState([]);
    const [selectedShippingOption, setSelectedShippingOption] = useState(null);

    const [preTotal, setPreTotal] = useState(0);
    const [totalCost, setTotalCost] = useState(0);
    const [taxes, setTaxes] = useState(0);
    const [shippingData, setShippingData] = useState({});
    const [packageDetails, setPackageDetails] = useState([]);

    const [stripeUserData, setStripeUserData] = useState(null);
    const [stripeUserDataId, setStripeUserDataId] = useState(null);
    const [stripeUserAddressHistory, setStripeUserAddressHistory] = useState([]);

    const [destinationCountrySelect, setDestinationCountrySelect] = useState(null);
    const [destinationProvinceSelect, setDestinationProvinceSelect] = useState(null);
    const [destinationProvinceSelectAutoComplete, setDestinationProvinceSelectAutoComplete] = useState(null);
    const [billingCountrySelect, setBillingCountrySelect] = useState(null);
    const [billingProvinceSelect, setBillingProvinceSelect] = useState(null);
    const [saveAddress, setSaveAddress] = useState(false);

    const [couponCode, setCouponCode] = useState('');
    const [couponAmount, setCouponAmount] = useState(null);
    const [giftcardAmount, setGiftcardAmount] = useState(null);
    const [freeShipping, setFreeShipping] = useState(false);
    const [goodErrorMessage, setGoodErrorMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [warningMessage, setWarningMessage] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (couponCode) {
            handleCouponChange(couponCode, destinationEmail, destinationCountry); // Trigger coupon check
        }
        else {
            setGoodErrorMessage('');
            setWarningMessage('');
            setErrorMessage('');

            setCouponAmount(null);
            setFreeShipping(false);
        }
    }, [couponCode, destinationCountry]); // Trigger the effect when couponCode changes

    const handleCouponChange = debounce(async (code, email, region) => {
        if (code) {
            try {
                // Make an API call to check the coupon code availability
                const couponResult = await getCouponConfirmation(code, email, region);

                if (!couponResult.success) {
                    setGoodErrorMessage('');
                    
                    if (couponResult.warning){
                        setWarningMessage(couponResult.message);
                        setErrorMessage('');
                    }
                    else {
                        setErrorMessage(couponResult.message || 'Coupon not valid');
                        setWarningMessage('');
                    }

                    setCouponAmount(null);
                    setGiftcardAmount(null);
                    setFreeShipping(false);
                } else {
                    // Successfully applied coupon
                    setErrorMessage(''); // Clear error message if valid
                    setWarningMessage('');
                    setGoodErrorMessage('Coupon is valid!');

                    setCouponAmount(couponResult.discount_amount ? couponResult.discount_amount : null);

                    const cardAmountCAD = couponResult.card_amount ? parseFloat(couponResult.card_amount/100).toFixed(2) : null;
                    const cardAmountConverted = cardAmountCAD ? formatCadPrice(cardAmountCAD, false) : null;
                    setGiftcardAmount(cardAmountConverted);
                    setFreeShipping(!!couponResult.free_shipping);
                }
            } catch (error) {
                console.error('Error validating coupon:', error);
                setGoodErrorMessage('');
                setErrorMessage('Failed to validate coupon');
            }
        }
    }, 250); // Delay for 500ms before calling the API after the user stops typing

    const handleShippingOptionChange = (option) => {
        setSelectedShippingOption(option);
    };

    const displayedShippingOptions = freeShipping && shippingOptions.length > 0 
        ? [shippingOptions[0]]
        : shippingOptions;

    const handleBillingAddressToggle = (e) => {
        setShowBillingAddress(!e.target.checked);
    };

    const handleSaveAddressToggle = (e) => {
        setSaveAddress(!saveAddress);
    };
      
    const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 6);
    const part3 = digits.slice(6, 10);
    
    if (digits.length <= 3) return part1;
    if (digits.length <= 6) return `${part1}-${part2}`;
    return `${part1}-${part2}-${part3}`;
    };

    useEffect(() => {
        const targetElement = document.getElementById("payment");
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: "smooth",
                block: "center" });
        }

    }, [clientSecret]);

    useEffect(() => {
        const setCountry = () => {
            setDestinationCountry(destinationCountrySelect?.iso2);
        }
        setCountry();
    }, [destinationCountrySelect]);

    useEffect(() => {
    const syncStateAutofill = async () => {
        const input = document.getElementById('province');
        const stateName = input?.value?.trim().toLowerCase();
        
        if (stateName && destinationCountrySelect?.id) {
        try {
            const states = await GetState(parseInt(destinationCountrySelect.id));
            const matchedState = states.find(
            (state) => state.name.toLowerCase() === stateName
            );
            if (matchedState) {
            setDestinationProvinceSelect(matchedState);
            }
        } catch (err) {
            console.error("State autofill sync failed:", err);
        }
        }
    };

    syncStateAutofill();
    }, [destinationCountrySelect?.id]);


    useEffect(() => {
        const setProvince = () => {
            setDestinationProvince(destinationProvinceSelect?.state_code);
        }
        setProvince();
    }, [destinationProvinceSelect]);

    useEffect(() => {
        const setCountry = () => {
            setBillingCountry(billingCountrySelect?.iso2);
        }
        setCountry();
    }, [billingCountrySelect]);

    useEffect(() => {
        const setProvince = () => {
            setBillingProvince(billingProvinceSelect?.state_code);
        }
        setProvince();
    }, [billingProvinceSelect]);

    const parseAllAddresses = (metadata) => {
        const parsedArray = [];
        
        for (const key in metadata) {
            const jsonObject = Object.fromEntries(
                metadata[key].split(',').map(pair => pair.split(':'))
            );

            parsedArray.push({key: key, data: jsonObject});
        }
        
        return parsedArray;
    };

    useEffect(() => {
        async function fetchUserData() {
            if (user) {
                const data = await fetchUserDataFromBd(user.id);

                const stripeData = await fetchStripeUserData(data.stripe_id, t);
                setStripeUserData(stripeData);
                setStripeUserDataId(stripeData.data.customer.id);

                if (stripeData.data.customer.shipping?.address){
                    setDefaultShippingAddress(stripeData.data.customer.shipping.address, stripeData.data.customer);
                }
                
                const allParsedAddresses = parseAllAddresses(stripeData?.data?.customer?.metadata)
                const addressArray = allParsedAddresses.map(item => item.data); // Extract only "data"

                setStripeUserAddressHistory(addressArray);
            }
        }

        fetchUserData();
    }, [user]);

    const handleAddressChange = (event) => {
        const selectedAddress = JSON.parse(event.target.value);

        setDefaultShippingAddress(selectedAddress); // Example if you have a state for selected address
    };

    useEffect(() => {
        const fetchCartItems = async () => {
            try {
                const cart = await new Promise((resolve) =>
                    setTimeout(() => resolve(cartItems), 1000)
                );

                setAsyncCartItems(cart);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching cart items:', error);
            }
        };

        fetchCartItems();
    }, [cartItems]);

    useEffect(() => {
            const billingAddressData = showBillingAddress
                ? {
                    line1: billingAddress,
                    line2: billingUnit || null,
                    city: billingCity,
                    state: billingProvince,
                    postal_code: billingZip,
                    country: billingCountry,
                }
                : null;
    
            const shippingAddress = {
                line1: destinationAddress,
                line2: destinationUnit || null,
                city: destinationCity,
                state: destinationProvince,
                postal_code: destinationZip,
                country: destinationCountry,
            };

            if (selectedShippingOption) {
                const chargedShippingPrice = freeShipping ? 0 : selectedShippingOption?.price;

                fetch(`${process.env.REACT_APP_BACKEND_URL}products/payments`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        cartItems: asyncCartItems,
                        shippingAddress: shippingAddress,
                        billingAddress: billingAddressData,
                        firstName: destinationFirstName,
                        lastName: destinationLastName,
                        email: destinationEmail,
                        phone: destinationPhoneNumber,
                        shippingPrice: selectedShippingOption?.price,
                        chargedShippingPrice: chargedShippingPrice,
                        freeShipping: freeShipping,
                        taxes: taxes,
                        shippingInfo: {
                            name: selectedShippingOption?.courier_name,
                            price: selectedShippingOption?.price,
                            deliveryTime: selectedShippingOption?.transit_time + " days",
                        },
                        clientId: stripeUserData?.data?.customer?.id,
                        shippingReference: shippingAddress?.postal_code + "-" + selectedShippingOption?.courier_name,
                        couponValue: couponAmount,
                        cardAmount: giftcardAmount || 0,
                        couponCode: couponCode,
                        currency: localStorage.getItem('selectedCurrency'),
                    }),
                })
                    .then(async (res) => {
                        const text = await res.text();

                        if (!res.ok) {
                            throw new Error(text || `Payment request failed with status ${res.status}`);
                        }

                        if (!text) {
                            return {};
                        }

                        try {
                            return JSON.parse(text);
                        } catch (parseError) {
                            throw new Error(`Invalid server response: ${text}`);
                        }
                    })
                    .then((data) => {
                        setClientSecret(data?.clientSecret || null);
                        setStripeUserDataId(data?.customerId || null);
                    })
                    .catch((error) => {
                        console.error('Error fetching client secret:', error);
                        setClientSecret(null);
                        setStripeUserDataId(null);
                        setErrorMessage(error.message || 'Unable to create the payment. Please try again.');
                    });
            }
    }, [
        asyncCartItems,
        billingAddress,
        billingUnit,
        billingCity,
        billingCountry,
        billingProvince,
        billingZip,
        destinationAddress,
        destinationUnit,
        destinationCity,
        destinationCountry,
        destinationProvince,
        destinationZip,
        showBillingAddress,
        destinationFirstName,
        destinationLastName,
        destinationPhoneNumber,
        destinationEmail,
        selectedShippingOption,
        couponAmount,
        giftcardAmount,
        freeShipping
        // taxes
    ]);

    useEffect(() => {
        if (selectedShippingOption && couponCode && (couponAmount !== null || giftcardAmount !== null || freeShipping)) {
            setClientSecret(null); 
            setStripeUserDataId(null);
        }
    }, [couponAmount, giftcardAmount, freeShipping]);

    useEffect(() => {
        if (freeShipping && shippingOptions.length > 0 && selectedShippingOption !== shippingOptions[0]) {
            setSelectedShippingOption(shippingOptions[0]);
        }
    }, [freeShipping, shippingOptions]);
        
    useEffect(() => {
        if (selectedShippingOption) {
            const shippingPrice = freeShipping ? 0 : parseFloat(selectedShippingOption.price);
            const calculRabais = preTotal - (couponAmount/100 * preTotal) - (giftcardAmount);
            const total = (calculRabais > 0 ? calculRabais : 0) + shippingPrice;
            
            setTotalCost(total);
        }
    }, [selectedShippingOption, preTotal, couponAmount, giftcardAmount, freeShipping]);

    useEffect(() => {
        if (destinationCountrySelect)
            if (destinationProvinceSelectAutoComplete){
                GetState(parseInt(destinationCountrySelect.id)).then((result) => {
                    const state = result.find(state => state.name.toLowerCase() === destinationProvinceSelectAutoComplete.toLowerCase());
                    setDestinationProvinceSelect(state);
                });
            }
            else {
                GetState(parseInt(destinationCountrySelect.id)).then((result) => {
                    const state = result.find(state => state.name.toLowerCase() === destinationProvince?.toLowerCase());
                    setDestinationProvinceSelect(state);
                });
            }
    }, [destinationCountrySelect]);

    useEffect(() => {
        var hasCustoms = asyncCartItems.some(item => item.isCustom === true);

        const checkDropStatus = async () => {
            try {
                const ended = await getDropEnded();
                if (hasCustoms && ended) {
                    setIsModalOpen(true);
                }
            } catch (err) {
                console.error("Failed to check drop status", err);
                setIsModalOpen(false);
            }
        };
        checkDropStatus();
    }, [asyncCartItems]);

    const setDefaultShippingAddress = (address, customer) => {
        if (customer) {
            const nameParts = customer?.shipping?.name?.trim().split(' ');
            setDestinationFirstName(nameParts[0]);
            setDestinationLastName(nameParts[1]);
            setDestinationPhoneNumber(customer.shipping?.phone);
            setDestinationEmail(customer.email);
        }
        setDestinationAddress(address.line1);
        setDestinationUnit(address.line2 === "null" ? '' : address.line2 || '');
        setDestinationCity(address.city);
        setDestinationCountry(address.country);
        setDestinationProvince(address.state || address.province);
        setDestinationZip(address.postal_code || address.zip);

        var country = null;
        GetCountries().then((result) => {
            country = result.find(country => country.name.toLowerCase() === address.country.toLowerCase());
            setDestinationCountrySelect(country);
        });
    }
    
    const getAllShippingQuote = async (e) => {
        e.preventDefault();

        if (!destinationFirstName || !destinationLastName || !destinationPhoneNumber || !destinationEmail || !destinationAddress || !destinationCity || !destinationCountry || !destinationProvince || !destinationZip) {
            notifyError('Please fill all the fields');
            return;
        }

        setShippingLoading(true);
        
        try {

            var weight = 0;
            var dimensions = [];

            asyncCartItems.forEach((item) => {
                for (let i = 0; i < item.numberOfItems; i++) {
                    weight += item.weight;
                    dimensions.push(item.dimensions);
                }
                item.priceConverted = calculateConvertedTotalPrice(item.prcbdn);
                item.cc = selectedCurrency;
                item.cs = currencySymbol;
            });

            const dataPackage = {
                dimensions: dimensions
            }

            const packageDetails = await getPackingInfo(dataPackage);
            setPackageDetails(packageDetails);

            const data = {
                firstName: destinationFirstName,
                lastName: destinationLastName,
                phone: destinationPhoneNumber,
                email: destinationEmail,
                address: destinationAddress,
                unit: destinationUnit,
                city: destinationCity,
                country: destinationCountry,
                province: destinationProvince,
                zip: destinationZip,
                weight: weight,
                packageDetails: packageDetails.packages[0],
                value: preTotal,
                items: asyncCartItems,
                currency: localStorage.getItem('selectedCurrency')
            };

            if (data.packageDetails.weight < 0.45){
                data.packageDetails.weight = 0.45;
            }
            
            
            const shippingOptionsData = await getShippingQuote(data);
            
            const sortedShippingOptions = shippingOptionsData.sort((a, b) => a.price - b.price);
            setShippingData(data);
            setShippingOptions(sortedShippingOptions);
        } catch (error) {
            console.error('Error fetching shipping options:', error);
            setShippingOptions([]);
        }

        setShippingLoading(false);
    };

    // useEffect(() => {
    //     if (selectedShippingOption && asyncCartItems.length > 0) {
    //         // Prepare destination address and customer details
    //         const address = {
    //             line1: destinationAddress,
    //             line2: destinationUnit,
    //             city: destinationCity,
    //             state: destinationProvince,
    //             postal_code: destinationZip,
    //             country: destinationCountry,
    //         };

    //         const customerDetails = {
    //             email: destinationEmail,
    //             name: `${destinationFirstName} ${destinationLastName}`,
    //             address: address,
    //         };

            

    //         const fetchTaxes = async () => {
    //             try {
    //                 const taxesData = await getTaxes(asyncCartItems, address, customerDetails);
    //                 const shippingPrice = parseFloat(selectedShippingOption.total);
    //                 const taxesCalculated = parseFloat(taxesData / 100);
                    
    //                 const total = preTotal + shippingPrice + taxesCalculated;
    //                 setTotalCost(total);

    //                 setTaxes(taxesCalculated);
    //             } catch (error) {
    //                 console.error('Error fetching taxes:', error);
    //                 setTaxes(0);
    //             }
    //         };

    //         fetchTaxes(); // Fetch taxes here
    //     }
    // }, [selectedShippingOption, preTotal, asyncCartItems, destinationFirstName, destinationLastName, destinationEmail, destinationAddress, destinationUnit, destinationCity, destinationCountry, destinationProvince, destinationZip]);

    const options = {
        clientSecret,
    };

    const shippingDisclaimer = (destinationCountry === "US" || destinationCountry === "UM") && (
        <div className='shipping-disclamer-label'>
            <p><span style={{ color: 'red' }}>*</span> {t("checkout.tarifsNoticeLabel")}</p>
        </div>
    );

    return (
        <>
        <Loading isVisible={loading} />
        <MessageModal isOpen={!loading && isModalOpen}
        onClose={() => setIsModalOpen(false)}
        message={t("checkout.removeDropLabel")} 
        closeText={t("checkout.closeLabel")}/>
            {!loading && (
                <div className="page-padding">
                    <div className="account-update-container make-smaller">
                        <div className="checkout-full-container">
                            <div className="col-setup-no">
                                <div className="address-update-section width-override">
                                    <CheckoutSummary asyncCartItems={asyncCartItems} onPreTotalChange={setPreTotal} taxes={taxes} selectedShippingOption={selectedShippingOption} totalCost={totalCost} isTinyMobileView={isSmallView} couponAmount={couponAmount} giftcardAmount={giftcardAmount} freeShipping={freeShipping}/>
                                    <div className="input-container-info width-coupon">
                                        <label htmlFor="couponCode">{t("checkout.couponLabel")}</label>
                                        <input
                                            id="couponCode"
                                            placeholder={t("checkout.enterCouponLabel")}
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value)}
                                            required
                                        />
                                    </div>
                                    {errorMessage && <p className="error-message">{errorMessage}</p>}
                                    {warningMessage && <p className="error-message warning-message">{warningMessage}</p>}
                                    {goodErrorMessage && !errorMessage && !warningMessage && <p className="error-message good-message">{goodErrorMessage}</p>}
                                </div>
                                {clientSecret && (
                                    <div id="payment" className="address-update-section width-override">
                                        <h2>{t("checkout.paymentLabel")}</h2>
                                        <Elements key={clientSecret} stripe={stripePromise} options={options}>
                                            <CheckoutForm asyncCartItems={asyncCartItems} taxes={taxes} selectedShippingOption={selectedShippingOption} totalCost={totalCost} shippingData={shippingData} packageDetails={packageDetails} preTotal={preTotal} stripe_id={stripeUserDataId} saveAddress={saveAddress} couponCode={couponCode} email={destinationEmail}/>
                                        </Elements>
                                    </div>
                                )}

                                    <div className="col-setup-no">
                                        <form onSubmit={getAllShippingQuote} >
                                    <div className="address-update-section width-override beans">
                                        {!user && (
                                            <p className="account-creation-nudge"><strong><a href="/account/login" class="no-link-color">{t("checkout.createAccountLabel")}</a></strong> {t("checkout.toSaveTimeLabel")}</p>
                                        )}
                                        
                                        <h2>{t("checkout.customerLabel")}</h2>

                                        <div className="input-container-info">
                                            <label htmlFor="destinationFirstName">{t("update.firstNameLabel")}</label>
                                            <input
                                                id="destinationFirstName"
                                                placeholder={t("update.firstNameLabel")}
                                                value={destinationFirstName}
                                                onChange={(e) => setDestinationFirstName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="input-container-info">
                                            <label htmlFor="destinationLastName">{t("update.lastNameLabel")}</label>
                                            <input
                                                id="destinationLastName"
                                                placeholder={t("update.lastNameLabel")}
                                                value={destinationLastName}
                                                onChange={(e) => setDestinationLastName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="input-container-info">
                                            <label htmlFor="destinationPhone">{t("checkout.phoneNumberLabel")}</label>
                                            <input
                                                id="destinationPhone"
                                                type="tel"
                                                placeholder={t("checkout.phoneNumberLabel")}
                                                value={formatPhoneNumber(destinationPhoneNumber)}
                                                onChange={(e) => {
                                                    const digitsOnly = e.target.value.replace(/\D/g, '');
                                                    setDestinationPhoneNumber(digitsOnly);
                                                }}
                                                required
                                            />
                                        </div>
                                        <div className="input-container-info">
                                            <label htmlFor="destinationEmail">{t("account.emailLabel")}</label>
                                            <input
                                                id="destinationEmail"
                                                type="email"
                                                placeholder={t("account.emailLabel")}
                                                value={destinationEmail}
                                                onChange={(e) => setDestinationEmail(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="address-update-section width-override">
                                        {/* Shipping Address */}
                                        <div>
                                            <h3>{t("checkout.shippingAddressLabel")}</h3>
                                            {user && (
                                                stripeUserAddressHistory.length !== 0 ? (
                                                    <div className="input-container-info extra-padding">
                                                        <label>{t("update.addressBook")}</label>
                                                        <select name="addresses" id="address-select" onChange={handleAddressChange}>
                                                            {stripeUserAddressHistory.map((option, index) => (
                                                                <option key={index} value={JSON.stringify(option)}>
                                                                    {option.line1}
                                                                    {option.line2 && option.line2 !== "null" ? `, ${option.line2}` : ""} 
                                                                    {" | "} {option.city}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ) : (
                                                    <div className="input-container-info extra-padding">
                                                        <div class="checkbox-row">
                                                        <p>{t("checkout.saveAddressLabel")}</p>
                                                            <input
                                                            type="checkbox"
                                                            onChange={handleSaveAddressToggle}
                                                            />
                                                        </div>
                                                    </div>
                                                )
                                            )}

                                            <div className="input-container-info">
                                                <label htmlFor="destinationAddress">{t("update.addressLabel")}</label>
                                                <input
                                                    id="destinationAddress"
                                                    placeholder={t("update.addressLabel")}
                                                    value={destinationAddress}
                                                    onChange={(e) => setDestinationAddress(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="input-container-info">
                                                <label htmlFor="destinationUnit">{t("checkout.unitNumberLabel")}</label>
                                                <input
                                                    id="destinationUnit"
                                                    placeholder={t("checkout.unitNumberLabel")}
                                                    value={destinationUnit}
                                                    onChange={(e) => setDestinationUnit(e.target.value)}
                                                />
                                            </div>
                                            <div className="input-container-info">
                                                <label htmlFor="destinationCountry">{t("update.countryLabel")}</label>
                                                <CountrySelect
                                                    id="destinationCountry"
                                                    placeHolder={t("checkout.selectCountryLabel")}
                                                    onChange={(_country) => setDestinationCountrySelect(_country)}
                                                    defaultValue={destinationCountrySelect}
                                                    required
                                                 />
                                                <input
                                                    id="country"
                                                    name="country"
                                                    placeHolder={t("update.countryLabel")}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        opacity: 0,
                                                        pointerEvents: 'none',
                                                        zIndex: 2, 
                                                    }}
                                                    onChange={(e) => {
                                                        GetCountries().then((result) => {
                                                            const country = result.find(country => country.name.toLowerCase() === e.target.value.toLowerCase());
                                                            setDestinationCountrySelect(country);
                                                        });
                                                    }}
                                                />
                                            </div>                                            
                                            <div className="input-container-info">
                                                <label htmlFor="destinationProvince">{t("update.provinceLabel")}</label>
                                                <StateSelect 
                                                    countryid={destinationCountrySelect?.id}
                                                    id="destinationProvince"
                                                    placeHolder={t("update.provinceLabel")}
                                                    inputClassName=""
                                                    onChange={(_state) => setDestinationProvinceSelect(_state)}
                                                    defaultValue={destinationProvinceSelect}
                                                    required
                                                />
                                                <input
                                                    id="province"
                                                    name="province"
                                                    placeHolder={t("update.provinceLabel")}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        opacity: 0,
                                                        pointerEvents: 'none',
                                                        zIndex: 2, 
                                                    }}
                                                    onChange={(e) => {
                                                        setDestinationProvinceSelectAutoComplete(e.target.value);
                                                    }}
                                                />
                                            </div>
                                            <div className="input-container-info">
                                                <label htmlFor="destinationCity">{t("update.cityLabel")}</label>
                                                <input
                                                    id="destinationCity"
                                                    placeholder={t("update.cityLabel")}
                                                    value={destinationCity}
                                                    onChange={(e) => setDestinationCity(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="input-container-info">
                                                <label htmlFor="destinationZip">{t("update.postalCodeLabel")}</label>
                                                <input
                                                    id="destinationZip"
                                                    placeholder={t("update.postalCodeLabel")}
                                                    value={destinationZip}
                                                    onChange={(e) => {
                                                        setDestinationZip(e.target.value);
                                                    }}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* Use as Billing Address */}
                                        <div class="checkbox-row">
                                            <p>{t("checkout.useAsBillingLabel")}</p>
                                            <input
                                            type="checkbox"
                                            defaultChecked
                                            onChange={handleBillingAddressToggle}
                                            />
                                        </div>

                                        {/* Billing Address (Only if checkbox is unchecked) */}
                                        {showBillingAddress && (
                                            <div>
                                                <h3>{t("checkout.billingAddressLabel")}</h3>

                                                <div className="input-container-info">
                                                    <label htmlFor="billingAddress">{t("update.addressLabel")}</label>
                                                    <input
                                                        id="billingAddress"
                                                        placeholder={t("update.addressLabel")}
                                                        value={billingAddress}
                                                        onChange={(e) => setBillingAddress(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="input-container-info">
                                                    <label htmlFor="billingUnit">{t("checkout.unitNumberLabel")}</label>
                                                    <input
                                                        id="billingUnit"
                                                        placeholder={t("checkout.unitNumberLabel")}
                                                        value={billingUnit}
                                                        onChange={(e) => setBillingUnit(e.target.value)}
                                                    />
                                                </div>
                                                <div className="input-container-info" autoComplete="off">
                                                    <label htmlFor="billingCountry">{t("update.countryLabel")}</label>
                                                    <CountrySelect
                                                        id="billingCountry"
                                                        placeHolder={t("checkout.selectCountryLabel")}
                                                        onChange={(_country) => setBillingCountrySelect(_country)}
                                                    />
                                                    {/* <input
                                                        id="billingCountry"
                                                        placeholder="Country"
                                                        value={billingCountry}
                                                        onChange={(e) => setBillingCountry(e.target.value)}
                                                        required
                                                    /> */}
                                                </div>
                                                <div className="input-container-info" autoComplete="off">
                                                    <label htmlFor="billingProvince">{t("update.provinceLabel")}</label>
                                                    <StateSelect 
                                                        countryid={billingCountrySelect?.id}
                                                        id="billingProvince"
                                                        placeHolder={t("update.provinceLabel")}
                                                        inputClassName=""
                                                        onChange={(_state) => setBillingProvinceSelect(_state)}
                                                        defaultValue={billingProvinceSelect}
                                                    />
                                                    {/* <input
                                                        id="billingProvince"
                                                        placeholder={t("update.provinceLabel")}
                                                        value={billingProvince}
                                                        onChange={(e) => setBillingProvince(e.target.value)}
                                                        required
                                                    /> */}
                                                </div>
                                                <div className="input-container-info">
                                                    <label htmlFor="billingCity">{t("update.cityLabel")}</label>
                                                    <input
                                                        id="billingCity"
                                                        placeholder={t("update.cityLabel")}
                                                        value={billingCity}
                                                        onChange={(e) => setBillingCity(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="input-container-info">
                                                    <label htmlFor="billingZip">{t("update.postalCodeLabel")}</label>
                                                    <input
                                                        id="billingZip"
                                                        placeholder={t("update.postalCodeLabel")}
                                                        value={billingZip}
                                                        onChange={(e) => {
                                                            setBillingZip(e.target.value);
                                                        }}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Submit Button Inside Form */}
                                        <div className="button-container-get">
                                            <button className="form-button get-shipping" type="submit" disabled={shippingLoading}>
                                                {t("checkout.getShippingLabel")}
                                            </button>
                                        </div>
                                    </div>
                                    {shippingLoading ? (
                                        <div className="address-update-section width-override width-override">
                                            <SmallerLoading />
                                        </div>
                                ) : shippingOptions.length === 0 ? (
                                    <div></div>
                                ) :
                                (
                                    <div className="address-update-section width-override">
                                        {shippingDisclaimer}
                                        <ul className="shipping-options">
                                            {displayedShippingOptions
                                                .map((option, index) => (
                                                option != null && (
                                                <li key={index}>
                                                    <label
                                                        className={`shipping-option-label ${selectedShippingOption === option ? "selected" : ""}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="shippingOption"
                                                            value={option.courier_name}
                                                            onChange={() => handleShippingOptionChange(option)}
                                                            checked={selectedShippingOption === option}
                                                        />
                                                        {`${option.courier_name.toUpperCase()} | `}
                                                        {option.transit_time != null && `${option.transit_time} days | `}
                                                        {freeShipping ? (
                                                            <>
                                                                <span className="free-shipping-price">{currencySymbol} {formatPrice(option.price)}</span>
                                                                {" "}<span className="free-shipping-label free-shipping-label-white">{t("checkout.freeLabel")}</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                {currencySymbol}{formatPrice(option.price)}
                                                            </>
                                                        )}
                                                    </label>
                                                </li>
                                            )))}
                                        </ul>
                                    </div>
                                )}
                                        </form>
                                    </div>
                            </div>

                            <div className="checkout-cart col-setup">
                                {/* Customer details */}
                                <form onSubmit={getAllShippingQuote} >
                                    <div className="address-update-section width-override beans">
                                        {!user && (
                                            <p className="account-creation-nudge"><strong><a href="/account/login" class="no-link-color">{t("checkout.createAccountLabel")}</a></strong> {t("checkout.toSaveTimeLabel")}</p>
                                        )}

                                        <h2>{t("checkout.customerLabel")}</h2>

                                        <div className="input-container-info">
                                            <label htmlFor="destinationFirstName">{t("update.firstNameLabel")}</label>
                                            <input
                                                id="destinationFirstName"
                                                placeholder={t("update.firstNameLabel")}
                                                value={destinationFirstName}
                                                onChange={(e) => setDestinationFirstName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="input-container-info">
                                            <label htmlFor="destinationLastName">{t("update.lastNameLabel")}</label>
                                            <input
                                                id="destinationLastName"
                                                placeholder={t("update.lastNameLabel")}
                                                value={destinationLastName}
                                                onChange={(e) => setDestinationLastName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="input-container-info">
                                            <label htmlFor="destinationPhone">{t("checkout.phoneNumberLabel")}</label>
                                            <input
                                                id="destinationPhone"
                                                type="tel"
                                                placeholder={t("checkout.phoneNumberLabel")}
                                                value={formatPhoneNumber(destinationPhoneNumber)}
                                                onChange={(e) => {
                                                    const digitsOnly = e.target.value.replace(/\D/g, '');
                                                    setDestinationPhoneNumber(digitsOnly);
                                                }}
                                                required
                                            />
                                        </div>
                                        <div className="input-container-info">
                                            <label htmlFor="destinationEmail">{t("account.emailLabel")}</label>
                                            <input
                                                id="destinationEmail"
                                                type="email"
                                                placeholder={t("account.emailLabel")}
                                                value={destinationEmail}
                                                onChange={(e) => setDestinationEmail(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="address-update-section width-override">
                                        {/* Shipping Address */}
                                        <div>
                                            <h3>{t("checkout.shippingAddressLabel")}</h3>
                                            
                                            {user && (
                                                stripeUserAddressHistory.length !== 0 ? (
                                                    <div className="input-container-info extra-padding">
                                                        <label>{t("update.addressBook")}</label>
                                                        <select name="addresses" id="address-select" onChange={handleAddressChange}>
                                                            {stripeUserAddressHistory.map((option, index) => (
                                                                <option key={index} value={JSON.stringify(option)}>
                                                                    {option.line1}
                                                                    {option.line2 && option.line2 !== "null" ? `, ${option.line2}` : ""} 
                                                                    {" | "} {option.city}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ) : (
                                                    <div className="input-container-info extra-padding">
                                                        <div class="checkbox-row">
                                                        <p>{t("checkout.saveAddressLabel")}</p>
                                                            <input
                                                            type="checkbox"
                                                            onChange={handleSaveAddressToggle}
                                                            />
                                                        </div>
                                                    </div>
                                                )
                                            )}

                                            <div className="input-container-info">
                                                <label htmlFor="destinationAddress">{t("update.addressLabel")}</label>
                                                <input
                                                    id="destinationAddress"
                                                    placeholder={t("update.addressLabel")}
                                                    value={destinationAddress}
                                                    onChange={(e) => setDestinationAddress(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="input-container-info">
                                                <label htmlFor="destinationUnit">{t("checkout.unitNumberLabel")}</label>
                                                <input
                                                    id="destinationUnit"
                                                    placeholder={t("checkout.unitNumberLabel")}
                                                    value={destinationUnit}
                                                    onChange={(e) => setDestinationUnit(e.target.value)}
                                                />
                                            </div>
                                            <div className="input-container-info">
                                                <label htmlFor="destinationCountry">{t("update.countryLabel")}</label>
                                                <CountrySelect
                                                    id="destinationCountry"
                                                    placeHolder={t("checkout.selectCountryLabel")}
                                                    onChange={(_country) => setDestinationCountrySelect(_country)}
                                                    defaultValue={destinationCountrySelect}
                                                    required
                                                 />
                                                 <input
                                                    id="country"
                                                    name="country"
                                                    placeHolder={t("update.countryLabel")}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        opacity: 0,
                                                        pointerEvents: 'none',
                                                        zIndex: 2, 
                                                    }}
                                                    onChange={(e) => {
                                                        GetCountries().then((result) => {
                                                            const country = result.find(country => country.name.toLowerCase() === e.target.value.toLowerCase());
                                                            setDestinationCountrySelect(country);
                                                        });
                                                    }}
                                                />
                                            </div>                                            
                                            <div className="input-container-info">
                                                <label htmlFor="destinationProvince">{t("update.provinceLabel")}</label>
                                                <StateSelect 
                                                    countryid={destinationCountrySelect?.id}
                                                    id="destinationProvince"
                                                    placeHolder={t("update.provinceLabel")}
                                                    inputClassName=""
                                                    onChange={(_state) => setDestinationProvinceSelect(_state)}
                                                    defaultValue={destinationProvinceSelect}
                                                    required
                                                />
                                                <input
                                                    id="province"
                                                    name="province"
                                                    placeHolder={t("update.provinceLabel")}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        opacity: 0,
                                                        pointerEvents: 'none',
                                                        zIndex: 2, 
                                                        }}
                                                    onChange={(e) => {
                                                        setDestinationProvinceSelectAutoComplete(e.target.value);
                                                    }}
                                                />
                                            </div>
                                            <div className="input-container-info">
                                                <label htmlFor="destinationCity">{t("update.cityLabel")}</label>
                                                <input
                                                    id="destinationCity"
                                                    placeholder={t("update.cityLabel")}
                                                    value={destinationCity}
                                                    onChange={(e) => setDestinationCity(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="input-container-info">
                                                <label htmlFor="destinationZip">{t("update.postalCodeLabel")}</label>
                                                <input
                                                    id="destinationZip"
                                                    placeholder={t("update.postalCodeLabel")}
                                                    value={destinationZip}
                                                    onChange={(e) => {
                                                        setDestinationZip(e.target.value);
                                                    }}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* Use as Billing Address */}
                                        <div class="checkbox-row">
                                            <p>{t("checkout.useAsBillingLabel")}</p>
                                            <input
                                            type="checkbox"
                                            defaultChecked
                                            onChange={handleBillingAddressToggle}
                                            />
                                        </div>

                                        {/* Billing Address (Only if checkbox is unchecked) */}
                                        {showBillingAddress && (
                                            <div>
                                                <h3>{t("checkout.billingAddressLabel")}</h3>

                                                <div className="input-container-info">
                                                    <label htmlFor="billingAddress">{t("update.addressLabel")}</label>
                                                    <input
                                                        id="billingAddress"
                                                        placeholder={t("update.addressLabel")}
                                                        value={billingAddress}
                                                        onChange={(e) => setBillingAddress(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="input-container-info">
                                                    <label htmlFor="billingUnit">{t("checkout.unitNumberLabel")}</label>
                                                    <input
                                                        id="billingUnit"
                                                        placeholder={t("checkout.unitNumberLabel")}
                                                        value={billingUnit}
                                                        onChange={(e) => setBillingUnit(e.target.value)}
                                                    />
                                                </div>
                                                <div className="input-container-info">
                                                    <label htmlFor="billingCountry">{t("update.countryLabel")}</label>
                                                    <CountrySelect
                                                        id="billingCountry"
                                                        placeHolder={t("checkout.selectCountryLabel")}
                                                        onChange={(_country) => setBillingCountrySelect(_country)}
                                                    />
                                                    {/* <input
                                                        id="billingCountry"
                                                        placeholder="Country"
                                                        value={billingCountry}
                                                        onChange={(e) => setBillingCountry(e.target.value)}
                                                        required
                                                    /> */}
                                                </div>
                                                <div className="input-container-info">
                                                    <label htmlFor="billingProvince">{t("update.provinceLabel")}</label>
                                                    <StateSelect 
                                                        countryid={billingCountrySelect?.id}
                                                        id="billingProvince"
                                                        placeHolder={t("update.provinceLabel")}
                                                        inputClassName=""
                                                        onChange={(_state) => setBillingProvinceSelect(_state)}
                                                        defaultValue={billingProvinceSelect}
                                                    />
                                                    {/* <input
                                                        id="billingProvince"
                                                        placeholder={t("update.provinceLabel")}
                                                        value={billingProvince}
                                                        onChange={(e) => setBillingProvince(e.target.value)}
                                                        required
                                                    /> */}
                                                </div>
                                                <div className="input-container-info">
                                                    <label htmlFor="billingCity">{t("update.cityLabel")}</label>
                                                    <input
                                                        id="billingCity"
                                                        placeholder={t("update.cityLabel")}
                                                        value={billingCity}
                                                        onChange={(e) => setBillingCity(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="input-container-info">
                                                    <label htmlFor="billingZip">{t("update.postalCodeLabel")}</label>
                                                    <input
                                                        id="billingZip"
                                                        placeholder={t("update.postalCodeLabel")}
                                                        value={billingZip}
                                                        onChange={(e) => {
                                                            setBillingZip(e.target.value);
                                                        }}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Submit Button Inside Form */}
                                        <div className="button-container-get">
                                            <button className="form-button get-shipping" type="submit" disabled={shippingLoading}>
                                                {t("checkout.getShippingLabel")}
                                            </button>
                                        </div>
                                    </div>

                                    {shippingLoading ? (
                                        <div className="address-update-section width-override width-override">
                                            <SmallerLoading />
                                        </div>
                                ) : shippingOptions.length === 0 ? (
                                    <div></div>
                                ) :
                                (
                                    <div className="address-update-section width-override">
                                        {shippingDisclaimer}
                                        <ul className="shipping-options">
                                            {displayedShippingOptions
                                                .map((option, index) => (
                                                option != null && (
                                                <li key={index}>
                                                    <label
                                                        className={`shipping-option-label ${selectedShippingOption === option ? "selected" : ""}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="shippingOption"
                                                            value={option.courier_name}
                                                            onChange={() => handleShippingOptionChange(option)}
                                                            checked={selectedShippingOption === option}
                                                        />
                                                        {`${option.courier_name.toUpperCase()} | `}
                                                        {option.transit_time != null && `${option.transit_time} days | `}
                                                        {freeShipping ? (
                                                            <>
                                                                <span className="free-shipping-price">{currencySymbol} {formatPrice(option.price)}</span>
                                                                {" "}<span className="free-shipping-label free-shipping-label-white">{t("checkout.freeLabel")}</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                {currencySymbol}{formatPrice(option.price)}
                                                            </>
                                                        )}
                                                    </label>
                                                </li>
                                            )))}
                                        </ul>
                                    </div>
                                )}
                                </form>
                            </div>

                            <div className="col-setup">
                                <div className="address-update-section width-override">
                                    <CheckoutSummary asyncCartItems={asyncCartItems} onPreTotalChange={setPreTotal} taxes={taxes} selectedShippingOption={selectedShippingOption} totalCost={totalCost} isTinyMobileView={isSmallView} couponAmount={couponAmount} giftcardAmount={giftcardAmount} freeShipping={freeShipping}/>
                                    <div className="input-container-info width-coupon">
                                        <label htmlFor="couponCode">{t("checkout.couponLabel")}</label>
                                        <input
                                            id="couponCode"
                                            placeholder={t("checkout.enterCouponLabel")}
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value)}
                                            required
                                        />
                                    </div>
                                    {errorMessage && <p className="error-message">{errorMessage}</p>}
                                    {warningMessage && <p className="error-message warning-message">{warningMessage}</p>}
                                    {goodErrorMessage && !errorMessage && !warningMessage && <p className="error-message good-message">{goodErrorMessage}</p>}
                                    {/* <div id="express-checkout-element" style={{ margin: '20px 0' }}></div> */}
                                </div>
                                {clientSecret && (
                                    <div className="address-update-section width-override">
                                        <h2>{t("checkout.paymentLabel")}</h2>
                                        <Elements key={clientSecret} stripe={stripePromise} options={options}>
                                            <CheckoutForm asyncCartItems={asyncCartItems} taxes={taxes} selectedShippingOption={selectedShippingOption} totalCost={totalCost} shippingData={shippingData} packageDetails={packageDetails}  preTotal={preTotal} stripe_id={stripeUserDataId} saveAddress={saveAddress} couponCode={couponCode} email={destinationEmail}/>
                                        </Elements>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CheckoutPage;
